/**
 * Invite acceptance page — two-step flow.
 * URL: /accept-invite?email=...&code=...
 *
 * Step 1: Choose method — OAuth sign-in or username & password setup.
 * Step 2 (password): Pick a username, set password, create account.
 *
 * OAuth auto-activates the invited account on callback (handled server-side).
 */

import { useState, useEffect, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { StagingBanner } from '../ui/StagingBanner';
import { RubyLogo } from './RubyLogo';

interface OAuthProviders {
  google: boolean;
  microsoft: boolean;
  apple: boolean;
}

/** Derive a username suggestion from an email address (same logic as server). */
function suggestUsername(email: string): string {
  return email.split('@')[0].replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase();
}

type Step = 'choose' | 'password' | 'done';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('choose');

  // Password form fields
  const [chosenUsername, setChosenUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [finalUsername, setFinalUsername] = useState('');

  const [providers, setProviders] = useState<OAuthProviders | null>(null);

  useEffect(() => {
    const e = params.get('email') ?? '';
    setEmail(e);
    setCode(params.get('code') ?? '');
    setChosenUsername(suggestUsername(e));
  }, [params]);

  useEffect(() => {
    fetch('/api/auth/oauth/providers')
      .then(r => r.json())
      .then((data: OAuthProviders) => {
        setProviders(data);
        // Skip method-selection step if no OAuth providers — go straight to password form
        if (!data.google && !data.microsoft && !data.apple) {
          setStep('password');
        }
      })
      .catch(() => {
        setProviders({ google: false, microsoft: false, apple: false });
        setStep('password');
      });
  }, []);

  const hasSocialLogin = providers && (providers.google || providers.microsoft || providers.apple);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = chosenUsername.trim();
    if (!/^[a-zA-Z0-9_-]{3,30}$/.test(trimmed)) {
      setError('Username must be 3–30 characters: letters, numbers, underscore, or hyphen');
      return;
    }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const result = await apiFetch<{ username: string }>('/api/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify({ email, code, password, username: trimmed }),
      });
      setFinalUsername(result.username);
      setStep('done');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  const goToPasswordStep = () => {
    setError('');
    setStep('password');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <StagingBanner />
      <div className="flex-1 flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {/* Header */}
          <div className="flex flex-col items-center mb-6">
            <RubyLogo className="h-10 mb-4" />
            <h1 className="text-xl font-bold text-gray-900">
              {step === 'done' ? 'Account Created' : step === 'password' ? 'Create Your Account' : 'Set Up Your Account'}
            </h1>
            {step === 'choose' && email && (
              <p className="text-sm text-gray-500 mt-1">{email}</p>
            )}
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* ── Step: Done ── */}
          {step === 'done' && (
            <div className="text-center space-y-4">
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-left">
                <p className="font-medium mb-1">Your account is ready!</p>
                <p>Your username is: <code className="bg-green-100 px-1.5 py-0.5 rounded font-mono">{finalUsername}</code></p>
              </div>
              <Link
                to="/"
                className="block w-full py-2.5 px-4 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 text-center transition-colors"
              >
                Sign in
              </Link>
            </div>
          )}

          {/* ── Step 1: Choose method ── */}
          {step === 'choose' && (
            <div className="space-y-4">
              {/* OAuth buttons */}
              {hasSocialLogin && (
                <>
                  <p className="text-sm text-gray-500 text-center">
                    Sign in with your existing account to get started:
                  </p>
                  <div className="space-y-3">
                    {providers!.google && (
                      <a href="/api/auth/oauth/google" className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                        <GoogleIcon /> Continue with Google
                      </a>
                    )}
                    {providers!.microsoft && (
                      <a href="/api/auth/oauth/microsoft" className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                        <MicrosoftIcon /> Continue with Microsoft
                      </a>
                    )}
                    {providers!.apple && (
                      <a href="/api/auth/oauth/apple" className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
                        <AppleIcon /> Continue with Apple
                      </a>
                    )}
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-400">or</span>
                    </div>
                  </div>
                </>
              )}

              {/* Password setup button */}
              <button
                onClick={goToPasswordStep}
                className="w-full py-2.5 px-4 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Set up with username &amp; password
              </button>
            </div>
          )}

          {/* ── Step 2: Username + Password form ── */}
          {step === 'password' && (
            <>
              {hasSocialLogin && (
                <button
                  type="button"
                  onClick={() => { setError(''); setStep('choose'); }}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 -mt-2 transition-colors"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={email} readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    required
                    minLength={3}
                    maxLength={30}
                    value={chosenUsername}
                    onChange={e => setChosenUsername(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Choose a username"
                  />
                  <p className="mt-1 text-xs text-gray-400">Letters, numbers, underscore, or hyphen. This is what you'll use to sign in.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Min 8 characters" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input type="password" required minLength={8} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Confirm password" />
                </div>
                <button type="submit" disabled={submitting}
                  className="w-full py-2.5 px-4 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {submitting ? 'Setting up...' : 'Create Account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons (same as LoginPage — small, no extra dependency)
// ---------------------------------------------------------------------------

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.1 13.28c-.3.68-.65 1.3-1.06 1.87-.56.78-1.02 1.32-1.37 1.62-.55.5-1.13.75-1.76.77-.45 0-.99-.13-1.62-.39-.63-.26-1.21-.39-1.74-.39-.56 0-1.15.13-1.79.39-.64.26-1.15.4-1.55.41-.6.03-1.2-.24-1.78-.79-.38-.33-.86-.89-1.42-1.7-.6-.85-1.1-1.84-1.48-2.97C.18 10.7 0 9.33 0 8.02c0-1.5.32-2.79.97-3.87a5.7 5.7 0 012.05-2.06A5.51 5.51 0 015.78 1.2c.48 0 1.1.15 1.88.44.77.29 1.27.44 1.49.44.16 0 .72-.18 1.65-.53.89-.33 1.63-.47 2.24-.42 1.66.13 2.9.79 3.72 1.97-1.48.9-2.21 2.16-2.19 3.78.02 1.26.47 2.31 1.35 3.14.4.38.85.67 1.34.88-.11.31-.22.61-.34.9zM11.53.37c0 .99-.36 1.91-1.08 2.78-.86 1.01-1.9 1.6-3.04 1.5a3.05 3.05 0 01-.02-.37c0-.95.41-1.96 1.14-2.79.36-.42.83-.76 1.39-1.04.56-.27 1.09-.42 1.58-.45.02.12.03.25.03.37z" fill="currentColor" />
    </svg>
  );
}
